import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirmDialog/confirmDialog.component';
import { forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { RolePermissionMatrixComponent } from 'src/app/components/role-permission-matrix/role-permission-matrix.component';
import { ObjectIdLookup } from 'src/app/models/objectIdLookup.model';
import { RolePermission } from 'src/app/models/rolePermission.model';
import { RoleRailItem } from 'src/app/models/roleRailItem.model';
import { UserRole } from 'src/app/models/userRole.model';
import { UserRoleService } from 'src/app/services/userRole.service';
import { ToastService } from 'src/app/services/toast.service';
import {
  EMPTY_ROLE_PERMISSION_DIFF,
  RolePermissionDiff,
  diffRolePermissions,
} from 'src/app/shared/utils/role-permission-diff.util';
import { computeSaveDivergence } from 'src/app/shared/utils/role-permission-divergence.util';
import { RolePermissionSaveResult } from 'src/app/components/role-permission-save-result-banner/role-permission-save-result-banner.component';
import { countGrantedRolePermissions, isIntegrationRoleName, isSystemRole } from 'src/app/shared/utils/role-permission-role.util';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { usersRouteConfig } from 'src/app/shared/constants/usersRouteConfig.constants';
import { ViewAsService } from 'src/app/services/view-as.service';
import { openViewAsDialog } from 'src/app/modals/view-as-dialog/view-as-dialog.component';

/** Load lifecycle of the shell (design-spec §7 states 1, 2). */
export type RolePermissionsLoadState = 'loading' | 'error' | 'ready';

/**
 * Master–detail shell for the Roles tab (DASH-3179, part 3/8; design-spec §4.1, §4.2).
 *
 * Owns the data load, the rail view models and role selection; hosts the {@link RoleRailComponent}
 * master column and the detail pane. The permission matrix toolbar, the matrix, and the save bar
 * mount inside `.detail-body` in later parts (4–7); this slice is the layout, the rail wiring and
 * the load / error / empty-roles states, with no visual change to the live screen yet (the route
 * still renders the legacy component until the final part swaps it).
 *
 * `OnPush`: the rail items are recomputed immutably on every load / add / select, so the template
 * needs no function calls (CLAUDE.md performance rule) and change detection stays cheap even with a
 * 300-role rail.
 */
@Component({
  standalone: false,
  selector: 'app-role-permissions-tab',
  templateUrl: './role-permissions-tab.component.html',
  styleUrls: ['./role-permissions-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolePermissionsTabComponent implements OnInit {
  /** Users-area tab links (Users / Roles / Organizations) — keeps the Roles view inside the shared tab framing. */
  tabs = usersRouteConfig;

  loadState: RolePermissionsLoadState = 'loading';
  railItems: RoleRailItem[] = [];
  selectedKey: string | null = null;
  selectedItem: RoleRailItem | null = null;

  objects: ObjectIdLookup[] = [];

  /** Unsaved-change summary for the selected role — drives the save bar (design-spec §5.6). */
  saveDiff: RolePermissionDiff = EMPTY_ROLE_PERMISSION_DIFF;
  /** Whether the selected role can be persisted right now (dirty/new, valid, not a protected system role). */
  canSave = false;
  /** True when only the selected role's name/description diverged from baseline — drives the save-bar's metadata-only dirty state. */
  metadataDirty = false;
  /** True when the selected role is a brand-new unsaved draft (added or cloned) — keeps the save bar actionable so it can be saved without an unrelated edit. */
  isNewRole = false;
  /** True while a save is in flight — shows the saving state and suppresses further edits/saves. */
  saving = false;
  /** Whether the "View as…" preview is offered for a role. */
  canStartViewAs = false;
  /** True during a read-only role preview: the role editing actions are hidden, since every write is rejected. */
  isReadonlyContext = false;

  /**
   * Outcome of the last save, from the mandatory read-back (design-spec §8, §7 states 7–8). `null` hides the
   * banner; `saved` is a clean success; `diverged` names what the server rewrote. Cleared on the next edit,
   * role switch, discard or the banner's dismiss.
   */
  saveResult: RolePermissionSaveResult | null = null;
  /** Ids the server rewrote on the last save — the matrix's amber edge, held until the banner is dismissed. */
  divergentObjectIds: ReadonlySet<string> = new Set<string>();

  /** The matrix instance, so the save-bar Review link can command it to the Changed filter (design-spec §5.6). */
  @ViewChild(RolePermissionMatrixComponent) private matrix?: RolePermissionMatrixComponent;

  private readonly draftKeys = new WeakMap<UserRole, string>();
  private draftCounter = 0;

  constructor(
    private readonly userRoleService: UserRoleService,
    private readonly toastService: ToastService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly viewAsService: ViewAsService,
  ) { }

  ngOnInit(): void {
    this.canStartViewAs = this.viewAsService.canStartViewAs();
    this.isReadonlyContext = this.viewAsService.isReadonlyContext;
    this.load();
  }

  /**
   * Loads roles and the permission-object catalogue together. The design forbids rendering an empty
   * matrix over a failed load (design-spec §7 state 2): on this screen "empty" means *no
   * permissions*, which is a lie. The shared service swallows HTTP errors into `of([])`, so an empty
   * object catalogue is treated as a load failure — the platform always returns ≥84 objects, so zero
   * can only mean the call failed. An empty *roles* list is legitimate and shows the empty-roles
   * state instead.
   */
  load(): void {
    this.loadState = 'loading';
    this.selectedKey = null;
    this.selectedItem = null;
    forkJoin({
      roles: this.userRoleService.getUserRoles(),
      objects: this.userRoleService.getObjectIds(),
    }).subscribe({
      next: ({ roles, objects }) => {
        if (!objects.length) {
          this.loadState = 'error';
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.objects = objects;
        this.railItems = roles.map(role => this.toRailItem(role));
        this.loadState = 'ready';
        this.selectFirst();
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        this.loadState = 'error';
        this.changeDetectorRef.markForCheck();
      },
    });
  }

  onSelectRole(item: RoleRailItem): void {
    this.select(item);
  }

  /**
   * Applies a verb toggle from the matrix (part 4). The new mask is written immutably — a new
   * permission array, role and rail item — so the `OnPush` matrix and rail re-render and the granted
   * count stays live. Full dirty tracking and the save write land with the save bar in part 6; here
   * the edit is held in memory on the selected role.
   */
  onPermissionChange(item: RoleRailItem, change: RolePermission): void {
    this.applyPermissionChanges(item, [change]);
  }

  /**
   * Applies a group-header bulk-set from the matrix (part 5): one verb written across every object in a
   * group. Uses the same immutable write path as a single toggle, so the granted count, header and rail
   * stay live and the save path (part 6) sees one consistent state.
   */
  onBulkVerbChange(item: RoleRailItem, changes: RolePermission[]): void {
    this.applyPermissionChanges(item, changes);
  }

  /** Header name edit (design-spec §5.4, DS-7): writes the role name in place. */
  onNameChange(item: RoleRailItem, event: Event): void {
    this.applyMetaChange(item, { name: (event.target as HTMLInputElement).value });
  }

  /** Header description edit (design-spec §5.4, DS-7): writes the role description in place. */
  onDescriptionChange(item: RoleRailItem, event: Event): void {
    this.applyMetaChange(item, { description: (event.target as HTMLInputElement).value });
  }

  /**
   * Header Require MFA toggle: blocked for integration-service roles, matching the backend rule that
   * rejects the flag for any role carrying an `IntegrationType` (`UserRoleService.Validate`).
   */
  onRequireMfaChange(item: RoleRailItem, checked: boolean): void {
    if (item.isIntegrationRole) {
      return;
    }
    this.applyMetaChange(item, { requireMfa: checked });
  }

  /**
   * Switches the matrix to the Changed filter in response to the save-bar Review link (design-spec §5.6),
   * so only the edited rows show. Filter ownership stays on the matrix (it persists across role switches).
   */
  reviewChanges(): void {
    this.matrix?.showChanged();
  }

  /**
   * Writes a header name/description edit onto the selected role immutably, then recomputes dirty state.
   * System roles are backend-protected and their header is read-only, so edits are rejected here too;
   * edits are also frozen mid-save. A rename registers as dirty without touching the permission diff.
   */
  private applyMetaChange(
    item: RoleRailItem,
    patch: Partial<Pick<UserRole, 'name' | 'description' | 'requireMfa'>>,
  ): void {
    if (item.isSystem || this.saving) {
      return;
    }
    this.clearSaveResult();
    const role: UserRole = { ...item.role, ...patch };
    const updated: RoleRailItem = {
      ...item,
      role,
      name: role.name,
      description: role.description,
      requireMfa: role.requireMfa,
      // Re-derive from the live name on every edit — a rename in this same session can gain or lose
      // the integration-service naming convention, and the stale flag from `toRailItem` at load time
      // must not keep locking (or unlocking) the toggle after that.
      isIntegrationRole: isIntegrationRoleName(role.name),
      isDirty: this.isDirty(role, item.baseline, item.baselineName, item.baselineDescription, item.baselineRequireMfa),
    };
    this.replaceItem(item.key, updated);
  }

  /**
   * Writes one or more object masks onto the selected role immutably — a new permission array, role and
   * rail item — so the `OnPush` matrix and rail re-render and the granted count stays live. Full dirty
   * tracking and the save write land with the save bar in part 6; here the edit is held in memory.
   */
  private applyPermissionChanges(item: RoleRailItem, changes: RolePermission[]): void {
    // System roles (Admin/User) are locked except for the AI Analyst row, which tenants must be able to
    // switch per built-in role. Edits are also frozen mid-save.
    const editable = item.isSystem ? changes.filter(change => change.objectId === RolePermissionObjects.AiAnalystAccess) : changes;
    if (this.saving || !editable.length) {
      return;
    }
    this.clearSaveResult();
    const maskByObjectId = new Map(editable.map(change => [change.objectId, change.permissions]));
    const merged = item.role.rolePermissions.map(permission =>
      maskByObjectId.has(permission.objectId)
        ? { ...permission, permissions: maskByObjectId.get(permission.objectId)! }
        : permission);
    // Objects not yet present on the role (e.g. a loaded role that never stored a row for one) are appended.
    const existingIds = new Set(item.role.rolePermissions.map(permission => permission.objectId));
    const added = editable.filter(change => !existingIds.has(change.objectId));
    const rolePermissions = [...merged, ...added];
    const role: UserRole = { ...item.role, rolePermissions };
    // `baseline` carries over via the spread; dirty is now the real diff against the last-saved snapshot.
    const updated: RoleRailItem = {
      ...item,
      role,
      grantedCount: this.countGranted(rolePermissions),
      isDirty: this.isDirty(role, item.baseline, item.baselineName, item.baselineDescription, item.baselineRequireMfa),
    };
    this.replaceItem(item.key, updated);
  }

  /**
   * Persists the selected role. Save is whole-role (design-spec §8): the full working mask is posted, the
   * server may rewrite it (per-object divergence read-back lands in part 8), and on success the baseline is
   * re-snapshotted so the role reads clean. A failure keeps the dirty state intact (§7 state 9).
   */
  save(item: RoleRailItem): void {
    if (this.saving || !this.canSave) {
      return;
    }
    const isUpdate = !!item.role.id;
    // Snapshot exactly what we post, so the read-back can be diffed against it for divergence (§8).
    const posted = this.snapshot(item.role.rolePermissions);
    this.clearSaveResult();
    this.saving = true;
    this.changeDetectorRef.markForCheck();
    const request = isUpdate
      ? this.userRoleService.update(item.role)
      : this.userRoleService.create(item.role);
    // Chain the create/update with the mandatory read-back via switchMap (never nest `.subscribe()`); this
    // also unifies both HTTP calls under the single error observer below (design-spec §8).
    request
      .pipe(
        switchMap(saved => {
          const savedId = isUpdate ? item.role.id : (saved as UserRole)?.id;
          return this.userRoleService.getUserRoles().pipe(map(roles => ({ savedId, roles })));
        }),
      )
      .subscribe({
        next: ({ savedId, roles }) => this.onSaved(item, savedId, posted, roles),
        error: (error: HttpErrorResponse) => {
          this.saving = false;
          // showError renders a structured/string server error body (avoids `[object Object]`); the FE
          // fallback is translated so es-CL tenants don't see raw English when the server returns no usable message.
          this.toastService.showError(
            error,
            this.translateService.instant('Nothing was saved. Your changes are still here.'),
          );
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  /** Reverts the selected role to its last-saved baseline (design-spec §5.6 Discard). */
  discard(item: RoleRailItem): void {
    // A system role can only be dirty through the AI Analyst row (see applyPermissionChanges).
    if (this.saving || (item.isSystem && !item.isDirty)) {
      return;
    }
    this.clearSaveResult();
    const role: UserRole = {
      ...item.role,
      name: item.baselineName,
      description: item.baselineDescription,
      requireMfa: item.baselineRequireMfa,
      rolePermissions: this.snapshot(item.baseline),
    };
    this.replaceItem(item.key, this.toRailItem(role));
  }

  /**
   * Handles a successful save with the mandatory read-back (design-spec §8): the server can rewrite the
   * payload (`StripUnentitledAnalystPermissionsAsync`), so the matrix is repainted from what was persisted,
   * never from the request. The read-back is fetched by `save()`'s `switchMap` and passed in here as `roles`
   * (same shape as the initial load), so the repaint and the new baseline are consistent. When the persisted
   * mask diverges from what was posted the amber divergence banner names the affected objects (§7 state 8);
   * otherwise the clean success banner and toast show (state 7). If the read-back can't be resolved the
   * working mask is re-baselined as before, so a save is never left looking unsaved.
   */
  private onSaved(
    item: RoleRailItem,
    savedId: string | undefined,
    posted: RolePermission[],
    roles: UserRole[],
  ): void {
    this.saving = false;
    const persisted = roles.find(role => role.id && role.id === savedId);
    if (!persisted) {
      // Read-back unavailable — fall back to the working mask so the save isn't shown as unsaved.
      this.replaceItem(item.key, this.toRailItem({ ...item.role, id: savedId ?? item.role.id }));
      this.setSaveResult({ status: 'saved', objects: [] }, new Set<string>(), true);
      return;
    }
    // Repaint and re-baseline from the persisted state — the server's stored mask is now the truth.
    this.replaceItem(item.key, this.toRailItem(persisted));
    const divergence = computeSaveDivergence(posted, persisted.rolePermissions, this.objects);
    if (divergence.objects.length) {
      this.setSaveResult({ status: 'diverged', objects: divergence.objects }, divergence.objectIds, false);
    } else {
      this.setSaveResult({ status: 'saved', objects: [] }, new Set<string>(), true);
    }
  }

  /** Publishes the save-result banner + matrix divergence edge; a clean save also raises the success toast. */
  private setSaveResult(
    result: RolePermissionSaveResult,
    divergentObjectIds: ReadonlySet<string>,
    toast: boolean,
  ): void {
    this.saveResult = result;
    this.divergentObjectIds = divergentObjectIds;
    if (toast) {
      this.toastService.showSuccess(
        'Role saved. Users holding this role pick up the change on their next request.',
        true,
      );
    }
    this.changeDetectorRef.markForCheck();
  }

  /** Dismisses the save-result banner and clears the matrix's divergence edge (design-spec §7 state 8). */
  dismissSaveResult(): void {
    this.clearSaveResult();
    this.changeDetectorRef.markForCheck();
  }

  /** Clears the last save outcome — on a new edit, a role switch, discard, a new save, or dismiss. */
  private clearSaveResult(): void {
    this.saveResult = null;
    this.divergentObjectIds = new Set<string>();
  }

  /** Swaps one rail item in place, keeps it selected if it was, and re-syncs the save bar. */
  private replaceItem(previousKey: string, updated: RoleRailItem): void {
    this.railItems = this.railItems.map(current => (current.key === previousKey ? updated : current));
    if (this.selectedKey === previousKey) {
      this.selectedItem = updated;
      this.selectedKey = updated.key;
      this.syncSelected();
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Creates an unsaved draft role, seeded with a zeroed permission for every catalogue object so the
   * save later writes the whole catalogue (design-spec §8, "omitted = revoked"). The draft is added
   * to the top of the rail and selected; it carries a stable client key until the server assigns an
   * id on save.
   */
  addRole(): void {
    const draft: UserRole = {
      name: '',
      description: '',
      requireMfa: false,
      rolePermissions: this.objects.map<RolePermission>(object => ({ objectId: object.id, permissions: 0 })),
    };
    const item = this.toRailItem(draft);
    this.railItems = [item, ...this.railItems];
    this.select(item);
  }

  /**
   * Clones the selected role into a new unsaved draft — the fastest way to author a role (design-spec §5.4;
   * the empty state even suggests "start from User or Admin and duplicate"). The clone copies the source
   * role's whole permission mask and description but drops its id and name (prefixed "Copy of …") so it
   * saves as a brand-new role; system roles can be cloned too, since the copy is an ordinary editable role.
   * The draft is added to the top of the rail and selected, mirroring {@link addRole}.
   */
  cloneRole(item: RoleRailItem): void {
    if (this.saving) {
      return;
    }
    const draft: UserRole = {
      name: this.translateService.instant('Copy of {{name}}', { name: item.role.name ?? '' }).trim(),
      description: item.role.description ?? '',
      // The clone's "Copy of …" name never matches the integration-service naming convention, so it is
      // always an ordinary editable role even when the source is one (which could only be requireMfa:
      // false anyway, since the backend rejects the flag on integration roles).
      requireMfa: item.role.requireMfa ?? false,
      rolePermissions: this.snapshot(item.role.rolePermissions),
    };
    const clone = this.toRailItem(draft);
    this.railItems = [clone, ...this.railItems];
    this.select(clone);
    this.changeDetectorRef.markForCheck();
  }

  /** Opens the view-as picker pre-filled with this role; only persisted roles can be previewed. */
  viewAsRole(item: RoleRailItem): void {
    if (!item.role.id || this.saving) {
      return;
    }
    openViewAsDialog(this.dialog, item.role.id);
  }

  /**
   * Deletes the selected role. An unsaved draft has no server row yet, so it is simply dropped from the
   * rail. A persisted, non-system role is deleted through a confirmation dialog whose primary action calls
   * the delete endpoint (the dialog owns the success/error toast); on confirmation the role is removed from
   * the rail and the first remaining role is selected. System roles are backend-protected and never expose
   * the affordance, so they are guarded here too; deletes are frozen mid-save.
   */
  deleteRole(item: RoleRailItem): void {
    if (item.isSystem || this.saving) {
      return;
    }
    if (!item.role.id) {
      this.removeItem(item.key);
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('Delete role?'),
        message: this.translateService.instant('Deleting this role removes it permanently. Users holding it must be reassigned to another role.'),
        primaryButtonText: this.translateService.instant('Delete'),
        // A successful DELETE returns HTTP 200 with an empty body, which HttpClient delivers as `null`.
        // Map it to `true` so the dialog closes with a truthy value distinguishable from a `false`
        // cancel and an `undefined` backdrop/ESC dismiss.
        primaryCallback: () => this.userRoleService.delete(item.role.id!).pipe(map(() => true)),
        successMessage: this.translateService.instant('Role deleted.'),
        secondaryButtonText: this.translateService.instant('Cancel'),
        errorMessage: this.translateService.instant('The role could not be deleted.'),
      } as ConfirmDialogData,
      width: '440px',
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      // Only a confirmed, successful delete emits `true` (see primaryCallback). Cancel closes with
      // `false` and a backdrop/ESC dismiss with `undefined`; neither should touch the rail.
      if (confirmed === true) {
        this.removeItem(item.key);
      }
    });
  }

  /** Drops a role from the rail; if it was selected, clears the selection and selects the first remaining role. */
  private removeItem(key: string): void {
    this.railItems = this.railItems.filter(current => current.key !== key);
    if (this.selectedKey === key) {
      this.selectedKey = null;
      this.selectedItem = null;
      this.clearSaveResult();
      this.selectFirst();
    }
    this.changeDetectorRef.markForCheck();
  }

  private select(item: RoleRailItem): void {
    // The save result belongs to the role that was saved; switching roles clears it (and the matrix edge).
    this.clearSaveResult();
    this.selectedItem = item;
    this.selectedKey = item.key;
    this.syncSelected();
  }

  /** Recomputes the save-bar diff and Save affordance for the selected role. */
  private syncSelected(): void {
    const item = this.selectedItem;
    if (!item) {
      this.saveDiff = EMPTY_ROLE_PERMISSION_DIFF;
      this.canSave = false;
      this.metadataDirty = false;
      this.isNewRole = false;
      return;
    }
    this.saveDiff = diffRolePermissions(item.baseline, item.role.rolePermissions);
    // A brand-new draft (added/cloned) has no server id yet; the save bar treats it as actionable so a
    // fresh clone can be saved directly instead of only after an unrelated permission edit.
    this.isNewRole = !item.role.id;
    // A rename/re-description/Require MFA change on a saved role is dirty without a permission change; a
    // new draft's metadata is captured as its own baseline, so metadataDirty is meaningful only once the
    // role has an id.
    this.metadataDirty =
      !!item.role.id &&
      this.metaChanged(item.role, item.baselineName, item.baselineDescription, item.baselineRequireMfa);
    // Save is actionable for a valid role that is either dirty (permissions or metadata) or new; a system
    // role qualifies only when its whole diff is the AI Analyst row.
    const systemRoleEditable =
      this.saveDiff.objectCount > 0 &&
      [...this.saveDiff.dirtyObjectIds].every(objectId => objectId === RolePermissionObjects.AiAnalystAccess);
    this.canSave =
      (!item.isSystem || systemRoleEditable) &&
      !!item.role.name?.trim() &&
      !!item.role.description?.trim() &&
      (this.saveDiff.objectCount > 0 || this.metadataDirty || !item.role.id);
  }

  /**
   * A role is dirty when it is an unsaved draft, its name/description/Require MFA diverges from baseline,
   * or its working mask diverges from its baseline mask.
   */
  private isDirty(
    role: UserRole,
    baseline: RolePermission[],
    baselineName: string,
    baselineDescription: string,
    baselineRequireMfa: boolean,
  ): boolean {
    return (
      !role.id ||
      this.metaChanged(role, baselineName, baselineDescription, baselineRequireMfa) ||
      diffRolePermissions(baseline, role.rolePermissions).objectCount > 0
    );
  }

  /**
   * True when the role's (trimmed-insensitive-of-nullish) name, description or Require MFA flag differs
   * from its baseline.
   */
  private metaChanged(
    role: UserRole,
    baselineName: string,
    baselineDescription: string,
    baselineRequireMfa: boolean,
  ): boolean {
    return (
      (role.name ?? '') !== baselineName ||
      (role.description ?? '') !== baselineDescription ||
      (role.requireMfa ?? false) !== baselineRequireMfa
    );
  }

  /** Deep-copies a permission-mask list so a baseline snapshot never aliases the working array. */
  private snapshot(permissions: RolePermission[]): RolePermission[] {
    return permissions.map(permission => ({ ...permission }));
  }

  private selectFirst(): void {
    const [first] = this.railItems;
    if (first) {
      this.select(first);
    }
  }

  private countGranted(rolePermissions: RolePermission[]): number {
    const catalogueIds = new Set(this.objects.map(object => object.id));
    return countGrantedRolePermissions(rolePermissions.filter(permission => catalogueIds.has(permission.objectId)));
  }

  private toRailItem(role: UserRole): RoleRailItem {
    // Snapshot the current mask/name/description/requireMfa as the last-saved baseline; edits are diffed
    // against these.
    const baseline = this.snapshot(role.rolePermissions);
    const baselineName = role.name ?? '';
    const baselineDescription = role.description ?? '';
    const baselineRequireMfa = role.requireMfa ?? false;
    return {
      key: this.keyFor(role),
      role,
      name: role.name,
      description: role.description,
      grantedCount: this.countGranted(role.rolePermissions),
      totalCount: this.objects.length,
      isSystem: isSystemRole(role.name),
      requireMfa: baselineRequireMfa,
      isIntegrationRole: isIntegrationRoleName(role.name),
      isDirty: this.isDirty(role, baseline, baselineName, baselineDescription, baselineRequireMfa),
      baseline,
      baselineName,
      baselineDescription,
      baselineRequireMfa,
    };
  }

  /** Persisted id for saved roles, or a stable client key for unsaved drafts (used for trackBy and selection). */
  private keyFor(role: UserRole): string {
    if (role.id) {
      return role.id;
    }
    let key = this.draftKeys.get(role);
    if (!key) {
      key = `draft-${this.draftCounter++}`;
      this.draftKeys.set(role, key);
    }
    return key;
  }
}
